// How a length reads on screen, on paper and in an export. The drawing's unit
// setting is its Dimension style; unset means millimetres — the unit every input
// takes — so the figures a user reads are the figures they type. `auto` (mm / cm /
// m picked per figure) is still there as a deliberate choice. Pure (unit-tested).

import type { DimStyle } from '../shared/types';

/** The style a drawing uses: its own, else millimetres. */
export function drawingDimStyle(d: { dimStyle?: DimStyle }): DimStyle {
  return d.dimStyle ?? 'mm';
}

export interface LenUnitSpec { factor: number; suffix: string; decimals: number }

/** Pick mm / cm / m for a length by its size — the `auto` style. */
export function autoUnit(absMm: number): LenUnitSpec {
  if (absMm >= 1000) return { factor: 1 / 1000, suffix: 'm', decimals: 2 };
  if (absMm >= 10) return { factor: 1 / 10, suffix: 'cm', decimals: 1 };
  return { factor: 1, suffix: 'mm', decimals: 0 };
}

/** The unit a style writes a length of this size in (usa writes mm first). */
export function styleUnit(absMm: number, style: DimStyle): LenUnitSpec {
  switch (style) {
    case 'cm': return { factor: 1 / 10, suffix: 'cm', decimals: 1 };
    case 'm': return { factor: 1 / 1000, suffix: 'm', decimals: 2 };
    case 'auto': return autoUnit(absMm);
    default: return { factor: 1, suffix: 'mm', decimals: 0 };
  }
}

/** Feet-and-inches for a length in mm (nearest inch), e.g. 1234 → 4'-1". */
export function feetInches(mm: number): string {
  const totalIn = Math.abs(mm) / 25.4;
  let ft = Math.floor(totalIn / 12);
  let inch = Math.round(totalIn - ft * 12);
  if (inch === 12) { ft += 1; inch = 0; }
  return `${mm < 0 ? '-' : ''}${ft}'-${inch}"`;
}

/** A dimension figure in the drawing's style. */
export function formatDimension(mm: number, style: DimStyle): string {
  const u = styleUnit(Math.abs(mm), style);
  const base = `${(mm * u.factor).toFixed(u.decimals)} ${u.suffix}`;
  return style === 'usa' ? `${base}  ${feetInches(mm)}` : base;
}

/** A figure that falls short of a limit, rounded DOWN at its style's precision
 *  instead of to the nearest — so 999.6 mm reads 999 mm, and 996 mm 0.99 m,
 *  never the limit itself. (A non-negative figure; the 1e-9 absorbs the binary
 *  error of the scaling, as in 0.29 × 100.) */
export function formatDimensionDown(mm: number, style: DimStyle): string {
  const u = styleUnit(Math.abs(mm), style), f = 10 ** u.decimals;
  return formatDimension(Math.floor(mm * u.factor * f + 1e-9) / f / u.factor, style);
}

/** An x,y pair sharing one unit (auto picks it from the larger magnitude). */
export function formatCoordPair(x: number, y: number, style: DimStyle): string {
  const u = styleUnit(Math.max(Math.abs(x), Math.abs(y)), style);
  const f = (v: number): string => (v * u.factor).toFixed(u.decimals);
  return `x ${f(x)}  y ${f(y)} ${u.suffix}`;
}
